import { FC, ReactElement } from 'react';

import { Skeleton, Typography, Stack } from '@mui/material';
import { Activity, Bank, DiscordLogo, MediumLogo, TwitterLogo, YoutubeLogo } from 'phosphor-react';

import useAssetPrices from '~components/hooks/protocol/assets/useAssetPrices';
import { useAllVaultData } from '~components/hooks/protocol/vaults/useAllVaultData';
import { FullWidthDivider } from '~components/shared/divider';
import { NormalLink } from '~components/shared/link';
import { primaryColors } from '~components/theme/styles/colors';
import { columnGap } from '~components/theme/styles/mixins';
import { EDisplayType } from '~components/types';
import { displayFormatUnits } from '~components/utils/utils';
import { ENativeAsset } from '~config/types';

import { ImageVestaLogoV2 } from '../../images';
import { GenericInfoLabel } from '../../label';
import { HelpTooltip } from '../../tooltip/helpTooltip';

import { AccountPanel } from './accountPanel';

export const Ticker: FC<{
  title: string;
  value: string;
  helpTooltip: string;
  isIncrease?: boolean;
  isLoading: boolean;
}> = ({ helpTooltip, title, isLoading, value }) => {
  return (
    <Stack direction={'column'}>
      <Stack direction={'row'} alignItems={'center'} spacing={1}>
        <HelpTooltip title={helpTooltip}></HelpTooltip>
        <Typography variant={'label3'} color={primaryColors[600]}>
          {title}
        </Typography>
      </Stack>
      <>
        {isLoading ? (
          <Skeleton sx={{ ml: 0.5 }} width={30} />
        ) : (
          <Typography variant={'label2'}>{value}</Typography>
        )}
      </>
    </Stack>
  );
};

export const SocialLink: FC<{ href: string; icon: ReactElement; label: string }> = ({
  href,
  icon,
  label,
}) => {
  return (
    <NormalLink href={href} target={'_blank'} sx={{ textDecoration: 'none', '& :hover': { color: 'white' } }}>
      <Stack direction={'row'} alignItems={'center'} spacing={2}>
        {icon}
        <Typography variant={'body3'} sx={{ color: primaryColors[100] }}>
          {label}
        </Typography>
      </Stack>
    </NormalLink>
  );
};

export const AccountDialSystemStats: FC<{ hideSocials?: boolean; forceOpen?: boolean }> = ({
  hideSocials,
  forceOpen,
}) => {
  const { data: assetPriceData, isLoading: isAssetPriceLoading } = useAssetPrices();
  const { computed } = useAllVaultData();

  const vstaTokenPriceUSD = assetPriceData.market[ENativeAsset.VSTA];
  const vstTokenPriceUSD = assetPriceData.market[ENativeAsset.VST];

  return (
    <AccountPanel
      titleContent="System Statistics"
      titleIcon={<Activity size={22} color={primaryColors[400]} weight="bold" />}
      forceOpen={forceOpen}
    >
      <Stack spacing={2} mb={!hideSocials ? 3 : 0}>
        <GenericInfoLabel
          labelTitle="VSTA"
          labelValue={displayFormatUnits({
            value: vstaTokenPriceUSD,
            displayType: EDisplayType.USD,
          })}
          variant="vertical"
          toolTip={{ content: 'Price from Coingecko', placement: 'title-left' }}
          isLoading={isAssetPriceLoading}
        />
        <GenericInfoLabel
          labelTitle="VST"
          labelValue={displayFormatUnits({
            value: vstTokenPriceUSD,
            displayType: EDisplayType.USD,
            decimal: 3,
          })}
          variant="vertical"
          toolTip={{ content: 'VST PRICE', placement: 'title-left' }}
          isLoading={isAssetPriceLoading}
        />
        <GenericInfoLabel
          labelTitle="TOTAL VALUE LOCKED"
          labelValue={displayFormatUnits({
            value: computed?.totalCollateralUsd ?? 0,
            displayType: EDisplayType.USD,
          })}
          variant="vertical"
          toolTip={{
            content: 'The amount of collateral currently operating in Vesta!',
            placement: 'title-left',
          }}
          isLoading={!!computed?.isAnyVaultLoading}
        />
        <GenericInfoLabel
          labelTitle="TOTAL DEBT"
          labelValue={displayFormatUnits({
            value: computed?.totalSystemDebtVst ?? 0,
            displayType: EDisplayType.VST,
          })}
          variant="vertical"
          toolTip={{
            content: 'The amount of VST minted by users on Vesta!',
            placement: 'title-left',
          }}
          isLoading={!!computed?.isAnyVaultLoading}
        />
      </Stack>

      {!hideSocials ? (
        <>
          <Stack sx={columnGap(16)}>
            <FullWidthDivider offsetPadding={32} sx={{ margin: 0 }} />
            <Typography variant={'label3'} color={primaryColors[700]}>
              SOCIALS MEDIA
            </Typography>
            <SocialLink
              label="Join our Discord Community!"
              href="https://discord.gg/yNc6Cm638"
              icon={<DiscordLogo size={22} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Follow us on Twitter!"
              href="https://twitter.com/vestafinance"
              icon={<TwitterLogo size={22} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Subscribe to our Medium!"
              href="https://medium.com/@VestaFinance"
              icon={<MediumLogo size={22} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Check out our community videos!"
              href=""
              icon={<YoutubeLogo size={22} color={primaryColors[400]} weight="regular" />}
            />
            <FullWidthDivider offsetPadding={32} sx={{ margin: 0 }} />
            <Typography variant={'label3'} color={primaryColors[700]}>
              STAY IN THE LOOP
            </Typography>
            <SocialLink
              label="Curia, our Governance Forum"
              href="https://curia.vestafinance.xyz/"
              icon={<Bank size={22} color={primaryColors[400]} weight="regular" />}
            />
            <SocialLink
              label="Documentation & White Paper"
              href="https://docs.vestafinance.xyz/"
              icon={<ImageVestaLogoV2 background={primaryColors[400]} width={22} height={22} />}
            />
          </Stack>
        </>
      ) : null}
    </AccountPanel>
  );
};
